import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  Users, TrendingUp, AlertCircle, CheckCircle2, Pause, Play, Percent,
  Plus, X, Loader2, FileText, BadgeCheck, Edit3, Link as LinkIcon, ExternalLink, Bell,
} from "lucide-react";
import TenantStatusBoard from "../../components/superadmin/TenantStatusBoard";

const CYCLES = ["monthly", "yearly", "one_time"];

function StatusPill({ active }) {
  return (
    <span
      className={`text-xs px-2 py-0.5 ${active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}
      data-testid={`tenant-status-${active ? "active" : "suspended"}`}>
      {active ? "Active" : "Suspended"}
    </span>
  );
}

export default function SuperAdminTenants() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [features, setFeatures] = useState([]);
  const [busy, setBusy] = useState(null);

  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({
    slug: "", name: "", owner_phone: "", owner_name: "", plan_slug: "pro", custom_domain: "",
  });
  const [createResult, setCreateResult] = useState(null);
  const [pending, setPending] = useState([]);

  const loadPending = async () => {
    try {
      const { data } = await api.get("/superadmin/pending-tenants");
      setPending(data || []);
    } catch { setPending([]); }
  };
  useEffect(() => { loadPending(); }, []);

  const [planPrice, setPlanPrice] = useState("");
  const [planCycle, setPlanCycle] = useState("monthly");
  const [discountPct, setDiscountPct] = useState("");
  const [discountAmt, setDiscountAmt] = useState("");
  const [discountNote, setDiscountNote] = useState("");
  const [grantKey, setGrantKey] = useState("");
  const [grantCycle, setGrantCycle] = useState("monthly");
  const [grantQty, setGrantQty] = useState(1);
  const [grantPrice, setGrantPrice] = useState("");
  const [grantNote, setGrantNote] = useState("");

  const load = async () => {
    try {
      const [t, f] = await Promise.all([
        api.get("/superadmin/tenants"),
        api.get("/superadmin/features"),
      ]);
      setRows(t.data);
      setFeatures(f.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load tenants");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const open = async (tid) => {
    setSelectedId(tid);
    setDetail(null);
    try {
      const { data } = await api.get(`/superadmin/tenants/${tid}`);
      setDetail(data);
      setPlanPrice(data.subscription?.price_at_purchase ?? "");
      setPlanCycle(data.subscription?.billing_cycle ?? "monthly");
      setDiscountPct(data.discount?.percent || "");
      setDiscountAmt(data.discount?.amount || "");
      setDiscountNote(data.discount?.note || "");
      setGrantKey(""); setGrantPrice(""); setGrantNote(""); setGrantQty(1); setGrantCycle("monthly");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load detail");
    }
  };
  const close = () => { setSelectedId(null); setDetail(null); };

  const refreshDetail = async () => {
    if (!selectedId) return;
    const { data } = await api.get(`/superadmin/tenants/${selectedId}`);
    setDetail(data);
    await load();
  };

  const run = async (label, fn) => {
    setBusy(label);
    try { await fn(); } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(null); }
  };

  const toggleSuspend = () =>
    run("suspend", async () => {
      if (detail.is_suspended) {
        await api.post(`/superadmin/tenants/${selectedId}/activate`);
        toast.success("Tenant activated");
      } else {
        const reason = window.prompt("Reason for suspension (optional)") || "";
        await api.post(`/superadmin/tenants/${selectedId}/suspend`, { reason });
        toast.success("Tenant suspended");
      }
      await refreshDetail();
    });

  const savePlan = () =>
    run("plan", async () => {
      const body = {};
      if (planPrice !== "" && Number(planPrice) >= 0) body.price_at_purchase = Number(planPrice);
      if (planCycle && planCycle !== detail.subscription?.billing_cycle) body.billing_cycle = planCycle;
      if (Object.keys(body).length === 0) { toast.info("Nothing changed"); return; }
      await api.patch(`/superadmin/tenants/${selectedId}/subscription`, body);
      toast.success("Plan updated");
      await refreshDetail();
    });

  const saveDiscount = () =>
    run("discount", async () => {
      await api.post(`/superadmin/tenants/${selectedId}/discount`, {
        discount_percent: Number(discountPct) || 0,
        discount_amount: Number(discountAmt) || 0,
        note: discountNote || null,
      });
      toast.success("Discount saved");
      await refreshDetail();
    });

  const clearDiscount = () =>
    run("clearDiscount", async () => {
      await api.delete(`/superadmin/tenants/${selectedId}/discount`);
      setDiscountPct(""); setDiscountAmt(""); setDiscountNote("");
      toast.success("Discount cleared");
      await refreshDetail();
    });

  const grantFeature = () =>
    run("grant", async () => {
      if (!grantKey) { toast.error("Pick a feature"); return; }
      await api.post(`/superadmin/tenants/${selectedId}/features/${grantKey}`, {
        billing_cycle: grantCycle,
        quantity: Number(grantQty) || 1,
        price_override: grantPrice === "" ? null : Number(grantPrice),
        note: grantNote || null,
      });
      toast.success("Feature granted");
      setGrantKey(""); setGrantPrice(""); setGrantNote(""); setGrantQty(1);
      await refreshDetail();
    });

  const updateAddon = (feature_key, patch) =>
    run(`update-${feature_key}`, async () => {
      await api.patch(`/superadmin/tenants/${selectedId}/features/${feature_key}`, patch);
      toast.success("Add-on updated");
      await refreshDetail();
    });

  const cancelAddon = (feature_key) =>
    run(`cancel-${feature_key}`, async () => {
      await api.delete(`/superadmin/tenants/${selectedId}/features/${feature_key}`);
      toast.success("Add-on cancelled");
      await refreshDetail();
    });

  const generateInvoice = () =>
    run("invoice", async () => {
      await api.post(`/superadmin/tenants/${selectedId}/invoices/generate`);
      toast.success("Invoice generated");
      await refreshDetail();
    });

  const markPaid = (invoiceId) =>
    run(`paid-${invoiceId}`, async () => {
      await api.post(`/superadmin/tenants/${selectedId}/invoices/${invoiceId}/mark-paid`);
      toast.success("Marked paid");
      await refreshDetail();
    });

  const grantableFeatures = useMemo(() => {
    const subscribed = new Set((detail?.addons || []).map((a) => a.feature_key));
    return (features || [])
      .filter((f) => f.is_active && !f.is_core_feature && !subscribed.has(f.feature_key));
  }, [features, detail]);

  const createTenant = async () => {
    if (!createForm.slug || !createForm.name || !createForm.owner_phone) {
      toast.error("Slug, name and owner phone are required");
      return;
    }
    setBusy("create");
    try {
      const payload = { ...createForm };
      if (!payload.custom_domain) delete payload.custom_domain;
      if (!payload.owner_name) delete payload.owner_name;
      const { data } = await api.post("/superadmin/tenants/create", payload);
      setCreateResult(data);
      toast.success(`Tenant ${data.tenant.slug} created`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create tenant");
    } finally { setBusy(null); }
  };

  const approvePending = async (tid) =>
    run(`approve-${tid}`, async () => {
      await api.post(`/superadmin/tenants/${tid}/approve`);
      toast.success("Tenant approved");
      await Promise.all([loadPending(), load()]);
    });

  const rejectPending = async (tid) =>
    run(`reject-${tid}`, async () => {
      await api.post(`/superadmin/tenants/${tid}/reject`);
      toast.success("Tenant rejected");
      await Promise.all([loadPending(), load()]);
    });

  const sendTestNotification = (tid) =>
    run(`notify-${tid}`, async () => {
      const { data } = await api.post(`/superadmin/tenants/${tid}/notify-test`);
      const sent = data.total_sent || 0;
      const admins = data.admin_count || 0;
      if (sent > 0) {
        toast.success(
          `Sent to ${sent} device${sent === 1 ? "" : "s"} across ${admins} admin${admins === 1 ? "" : "s"}`,
          {
            description: `Tenant: ${data.tenant?.name || data.tenant?.slug}. ` +
              `Shard: ${data.tenant?.firebase_project_id || "legacy"}.`,
          }
        );
      } else {
        // Build a diagnostic — common case: admin has never registered a token
        const noTokens = (data.per_admin || []).every(
          (a) => (a.fcm_tokens || 0) === 0 && (a.web_subscriptions || 0) === 0
        );
        toast.warning(
          noTokens
            ? "No device tokens found"
            : "Dispatch attempted but 0 devices received it",
          {
            description: noTokens
              ? "Owner must open the app, grant push permission, then retry."
              : "Check backend logs — token may be expired or shard misconfigured.",
            duration: 8000,
          }
        );
      }
    });

  return (
    <div className="space-y-6" data-testid="super-tenants-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl">Tenants</h1>
          <p className="text-sm text-[hsl(var(--kb-muted))] mt-1">
            Create new tenants, suspend / activate, grant features, override pricing and bill any tenant.
          </p>
        </div>
        <button
          onClick={() => { setShowCreate(true); setCreateResult(null); }}
          className="kb-btn-primary text-sm inline-flex items-center gap-1.5"
          data-testid="create-tenant-btn">
          <Plus size={14} /> Create tenant
        </button>
      </div>

      <TenantStatusBoard />

      {pending.length > 0 && (
        <section className="bg-amber-50 border-2 border-amber-300 p-5" data-testid="pending-tenants-section">
          <h2 className="font-display text-xl mb-3 flex items-center gap-2 text-amber-900">
            <AlertCircle size={18} /> {pending.length} pending tenant{pending.length > 1 ? "s" : ""} awaiting your approval
          </h2>
          <div className="space-y-2">
            {pending.map((t) => (
              <div key={t.id} className="bg-white border border-amber-200 p-4 flex flex-wrap items-center justify-between gap-3"
                   data-testid={`pending-tenant-${t.slug}`}>
                <div>
                  <div className="font-medium">{t.name}</div>
                  <div className="text-xs text-[hsl(var(--kb-muted))] font-mono">
                    /t/{t.slug}/ · phone {t.owner_phone} · plan {t.requested_plan_slug || "starter"}
                    {t.business_email ? ` · ${t.business_email}` : ""}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => approvePending(t.id)} disabled={busy === `approve-${t.id}`}
                          className="kb-btn-primary text-sm inline-flex items-center gap-1"
                          data-testid={`approve-${t.slug}`}>
                    {busy === `approve-${t.id}` ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Approve
                  </button>
                  <button onClick={() => rejectPending(t.id)} disabled={busy === `reject-${t.id}`}
                          className="kb-btn-outline text-sm inline-flex items-center gap-1"
                          data-testid={`reject-${t.slug}`}>
                    {busy === `reject-${t.id}` ? <Loader2 size={12} className="animate-spin" /> : <X size={12} />} Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-32 bg-[hsl(var(--kb-surface-alt))] animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {rows.map((t) => (
            <button key={t.id} onClick={() => open(t.id)}
              className="bg-white border border-[hsl(var(--kb-border))] p-5 text-left hover:border-[hsl(var(--kb-primary))]/40 transition"
              data-testid={`tenant-card-${t.id}`}>
              <div className="flex items-center gap-2 text-xs text-[hsl(var(--kb-muted))] justify-between">
                <span className="flex items-center gap-1"><Users size={14} /> Tenant</span>
                <StatusPill active={!t.is_suspended} />
              </div>
              <div className="font-display text-xl mt-1">{t.name || "Unnamed"}</div>
              <div className="text-xs text-[hsl(var(--kb-muted))] font-mono">{t.phone}</div>
              {t.tenant_slug && (
                <div className="text-xs text-[hsl(var(--kb-primary))] font-mono mt-1 flex items-center gap-2">
                  <LinkIcon size={10} /> {t.tenant_slug}
                  <a href={`/t/${t.tenant_slug}/`} target="_blank" rel="noreferrer"
                     onClick={(e) => e.stopPropagation()}
                     className="text-[10px] underline hover:text-[hsl(var(--kb-primary-hover))] inline-flex items-center gap-1"
                     data-testid={`visit-storefront-${t.tenant_slug}`}>
                    Visit <ExternalLink size={9} />
                  </a>
                </div>
              )}
              <div className="mt-3 space-y-1 text-sm">
                <div className="flex items-center gap-1"><CheckCircle2 size={12} className="text-green-600" /> Plan: <b>{t.plan_slug || "—"}</b> ({t.billing_cycle || "—"})</div>
                <div>Active features: {t.active_feature_count}</div>
                <div className="flex items-center gap-1"><TrendingUp size={12} /> Lifetime: ₹{t.lifetime_revenue.toLocaleString()}</div>
                {(t.discount_percent > 0 || t.discount_amount > 0) ? (
                  <div className="flex items-center gap-1 text-blue-700"><Percent size={12} /> Discount applied</div>
                ) : null}
                {t.outstanding_amount > 0 && (
                  <div className="flex items-center gap-1 text-amber-700"><AlertCircle size={12} /> Outstanding: ₹{t.outstanding_amount.toLocaleString()}</div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {selectedId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={close}>
          <div className="bg-white max-w-5xl w-full max-h-[92vh] overflow-y-auto p-6 space-y-6"
               onClick={(e) => e.stopPropagation()} data-testid="tenant-detail-dialog">
            {!detail ? (
              <div className="h-48 bg-[hsl(var(--kb-surface-alt))] animate-pulse" />
            ) : (
              <>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="font-display text-2xl">{detail.tenant.name}</h2>
                      <StatusPill active={!detail.is_suspended} />
                    </div>
                    <div className="text-xs text-[hsl(var(--kb-muted))] font-mono">{detail.tenant.phone} · {detail.tenant.id}</div>
                    {detail.tenant.tenant_suspended_reason && (
                      <div className="text-xs text-red-700 mt-1">Reason: {detail.tenant.tenant_suspended_reason}</div>
                    )}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={() => sendTestNotification(detail.tenant.id)}
                      disabled={busy === `notify-${detail.tenant.id}`}
                      className="text-sm px-3 py-1.5 inline-flex items-center gap-1.5 kb-btn-outline"
                      title="Dispatches a real push notification to every admin user of this tenant. Reuses the same FCM pipeline as order alerts."
                      data-testid="tenant-send-test-notification">
                      {busy === `notify-${detail.tenant.id}`
                        ? <Loader2 size={12} className="animate-spin" />
                        : <Bell size={12} />}
                      Send test notification
                    </button>
                    <button
                      onClick={toggleSuspend}
                      disabled={busy === "suspend"}
                      className={`text-sm px-3 py-1.5 inline-flex items-center gap-1.5 ${detail.is_suspended ? "kb-btn-primary" : "kb-btn-outline"}`}
                      data-testid="tenant-suspend-toggle">
                      {busy === "suspend" ? <Loader2 size={12} className="animate-spin" /> : (detail.is_suspended ? <Play size={12} /> : <Pause size={12} />)}
                      {detail.is_suspended ? "Activate" : "Suspend"}
                    </button>
                    <button onClick={close} className="kb-btn-outline text-sm" data-testid="tenant-dialog-close"><X size={14} /></button>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="border border-[hsl(var(--kb-border))] p-3"><div className="text-xs text-[hsl(var(--kb-muted))]">Plan</div><div className="font-medium">{detail.subscription?.plan_slug || "—"}</div></div>
                  <div className="border border-[hsl(var(--kb-border))] p-3"><div className="text-xs text-[hsl(var(--kb-muted))]">Cycle</div><div className="font-medium">{detail.subscription?.billing_cycle || "—"}</div></div>
                  <div className="border border-[hsl(var(--kb-border))] p-3"><div className="text-xs text-[hsl(var(--kb-muted))]">Add-ons</div><div className="font-medium">{detail.addons?.length || 0}</div></div>
                  <div className="border border-[hsl(var(--kb-border))] p-3"><div className="text-xs text-[hsl(var(--kb-muted))]">Active features</div><div className="font-medium">{detail.active_features?.length || 0}</div></div>
                </div>

                <section className="border border-[hsl(var(--kb-border))] p-4" data-testid="plan-override-section">
                  <h3 className="font-display text-lg flex items-center gap-2"><Edit3 size={14} /> Plan price override</h3>
                  <p className="text-xs text-[hsl(var(--kb-muted))] mt-1">Manually set this tenant's plan price without forcing a plan switch.</p>
                  <div className="mt-3 flex flex-wrap items-end gap-3">
                    <label className="text-xs flex flex-col">
                      <span>Price (₹)</span>
                      <input type="number" value={planPrice} onChange={(e) => setPlanPrice(e.target.value)} className="kb-input w-32" data-testid="plan-price-input" />
                    </label>
                    <label className="text-xs flex flex-col">
                      <span>Cycle</span>
                      <select value={planCycle} onChange={(e) => setPlanCycle(e.target.value)} className="kb-input w-32" data-testid="plan-cycle-input">
                        {CYCLES.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </label>
                    <button className="kb-btn-primary text-sm" onClick={savePlan} disabled={busy === "plan"} data-testid="plan-save-btn">
                      {busy === "plan" ? <Loader2 size={12} className="animate-spin" /> : null} Save plan price
                    </button>
                  </div>
                </section>

                <section className="border border-[hsl(var(--kb-border))] p-4" data-testid="discount-section">
                  <h3 className="font-display text-lg flex items-center gap-2"><Percent size={14} /> Discount</h3>
                  <p className="text-xs text-[hsl(var(--kb-muted))] mt-1">Applied to every invoice as a negative line item.</p>
                  <div className="mt-3 flex flex-wrap items-end gap-3">
                    <label className="text-xs flex flex-col">
                      <span>Percent (%)</span>
                      <input type="number" min="0" max="100" value={discountPct} onChange={(e) => setDiscountPct(e.target.value)} className="kb-input w-28" data-testid="discount-pct-input" />
                    </label>
                    <label className="text-xs flex flex-col">
                      <span>Flat (₹)</span>
                      <input type="number" min="0" value={discountAmt} onChange={(e) => setDiscountAmt(e.target.value)} className="kb-input w-28" data-testid="discount-amt-input" />
                    </label>
                    <label className="text-xs flex flex-col flex-1 min-w-[180px]">
                      <span>Note</span>
                      <input value={discountNote} onChange={(e) => setDiscountNote(e.target.value)} className="kb-input" placeholder="e.g. Loyalty bonus" data-testid="discount-note-input" />
                    </label>
                    <button className="kb-btn-primary text-sm" onClick={saveDiscount} disabled={busy === "discount"} data-testid="discount-save-btn">
                      {busy === "discount" ? <Loader2 size={12} className="animate-spin" /> : null} Save
                    </button>
                    <button className="kb-btn-outline text-sm" onClick={clearDiscount} disabled={busy === "clearDiscount"} data-testid="discount-clear-btn">Clear</button>
                  </div>
                  {detail.estimate?.discount_amount > 0 && (
                    <div className="mt-2 text-xs text-blue-700">Current cycle savings: ₹{detail.estimate.discount_amount.toLocaleString()}</div>
                  )}
                </section>

                <section className="border border-[hsl(var(--kb-border))] p-4" data-testid="addons-section">
                  <h3 className="font-display text-lg flex items-center gap-2"><BadgeCheck size={14} /> Active add-ons</h3>
                  {detail.addons?.length ? (
                    <div className="mt-3 space-y-2">
                      {detail.addons.map((a) => (
                        <AddonRow key={a.id} addon={a}
                                  onSave={(patch) => updateAddon(a.feature_key, patch)}
                                  onCancel={() => cancelAddon(a.feature_key)}
                                  busy={busy} />
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-[hsl(var(--kb-muted))] mt-2">No add-ons.</div>
                  )}
                </section>

                <section className="border border-[hsl(var(--kb-border))] p-4" data-testid="grant-section">
                  <h3 className="font-display text-lg flex items-center gap-2"><Plus size={14} /> Grant a feature manually</h3>
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-5 gap-3">
                    <select value={grantKey} onChange={(e) => setGrantKey(e.target.value)} className="kb-input md:col-span-2" data-testid="grant-feature-select">
                      <option value="">— pick a feature —</option>
                      {grantableFeatures.map((f) => <option key={f.feature_key} value={f.feature_key}>{f.feature_name}</option>)}
                    </select>
                    <select value={grantCycle} onChange={(e) => setGrantCycle(e.target.value)} className="kb-input" data-testid="grant-cycle-select">
                      {CYCLES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <input type="number" min="1" value={grantQty} onChange={(e) => setGrantQty(e.target.value)} className="kb-input" placeholder="Qty" data-testid="grant-qty-input" />
                    <input type="number" min="0" value={grantPrice} onChange={(e) => setGrantPrice(e.target.value)} className="kb-input" placeholder="Price (₹) — blank = catalog" data-testid="grant-price-input" />
                  </div>
                  <input value={grantNote} onChange={(e) => setGrantNote(e.target.value)} className="kb-input mt-3 w-full" placeholder="Internal note (optional)" data-testid="grant-note-input" />
                  <button className="kb-btn-primary text-sm mt-3" onClick={grantFeature} disabled={busy === "grant" || !grantKey} data-testid="grant-submit-btn">
                    {busy === "grant" ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Grant
                  </button>
                </section>

                <section className="border border-[hsl(var(--kb-border))] p-4" data-testid="invoices-section">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <h3 className="font-display text-lg flex items-center gap-2"><FileText size={14} /> Invoices</h3>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-xs text-[hsl(var(--kb-muted))]">Next bill estimate: <b>₹{detail.estimate?.total_amount?.toLocaleString() || 0}</b></span>
                      <button className="kb-btn-outline text-sm" onClick={generateInvoice} disabled={busy === "invoice"} data-testid="generate-invoice-btn">
                        {busy === "invoice" ? <Loader2 size={12} className="animate-spin" /> : null}
                        Generate invoice now
                      </button>
                    </div>
                  </div>
                  {detail.invoices?.length === 0 ? (
                    <div className="text-sm text-[hsl(var(--kb-muted))] mt-2">No invoices yet.</div>
                  ) : (
                    <div className="mt-3 space-y-2">
                      {detail.invoices.map((inv) => (
                        <div key={inv.id} className="border border-[hsl(var(--kb-border))] p-3 flex items-center justify-between text-sm flex-wrap gap-2" data-testid={`invoice-${inv.id}`}>
                          <div>
                            <div className="font-medium">₹{inv.total_amount.toLocaleString()} · {inv.billing_cycle}</div>
                            <div className="text-xs text-[hsl(var(--kb-muted))]">
                              {new Date(inv.billing_start_date).toLocaleDateString()} – {new Date(inv.billing_end_date).toLocaleDateString()} · {inv.line_items?.length || 0} line items
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className={`text-xs px-2 py-0.5 ${inv.status === "paid" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{inv.status}</span>
                            {inv.status !== "paid" && (
                              <button className="kb-btn-outline text-xs" onClick={() => markPaid(inv.id)} disabled={busy === `paid-${inv.id}`} data-testid={`invoice-mark-paid-${inv.id}`}>
                                {busy === `paid-${inv.id}` ? <Loader2 size={10} className="animate-spin" /> : null} Mark paid
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              </>
            )}
          </div>
        </div>
      )}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto"
             onClick={() => setShowCreate(false)}>
          <div className="bg-white max-w-lg w-full p-6 space-y-4"
               onClick={(e) => e.stopPropagation()} data-testid="create-tenant-dialog">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl">Create a tenant</h2>
              <button onClick={() => setShowCreate(false)} className="kb-btn-outline text-sm" data-testid="create-tenant-close"><X size={14} /></button>
            </div>
            {createResult ? (
              <div className="space-y-3" data-testid="create-tenant-result">
                <div className="bg-green-50 border border-green-200 p-4 text-sm">
                  <div className="font-semibold text-green-800">✓ {createResult.tenant.name} is ready.</div>
                  <div className="mt-3 space-y-1 text-green-900 font-mono text-xs">
                    <div>Slug: <b>{createResult.tenant.slug}</b></div>
                    <div>Storefront URL: <a className="underline" href={`/t/${createResult.tenant.slug}/`} target="_blank" rel="noreferrer">/t/{createResult.tenant.slug}/ <ExternalLink size={10} className="inline" /></a></div>
                    <div>Admin phone: <b>{createResult.owner.phone}</b></div>
                  </div>
                  <p className="mt-3 text-xs text-green-700">Share the storefront URL and admin login with the tenant. They sign in with the phone above and OTP shown in the modal.</p>
                </div>
                <button onClick={() => setShowCreate(false)} className="kb-btn-primary text-sm w-full" data-testid="create-tenant-done">Done</button>
              </div>
            ) : (
              <>
                <p className="text-sm text-[hsl(var(--kb-muted))]">Create a brand-new white-label tenant with its own catalog, customers, sellers and branding.</p>
                <label className="text-xs flex flex-col">
                  <span>URL slug (lowercase, dashes only)</span>
                  <input className="kb-input" value={createForm.slug} placeholder="e.g. nayaab-couture"
                         onChange={(e) => setCreateForm({ ...createForm, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })}
                         data-testid="create-tenant-slug" />
                </label>
                <label className="text-xs flex flex-col">
                  <span>Display name</span>
                  <input className="kb-input" value={createForm.name} placeholder="Nayaab Couture House"
                         onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                         data-testid="create-tenant-name" />
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <label className="text-xs flex flex-col">
                    <span>Owner phone (10 digit)</span>
                    <input className="kb-input" value={createForm.owner_phone} placeholder="9XXXXXXXXX"
                           onChange={(e) => setCreateForm({ ...createForm, owner_phone: e.target.value.replace(/[^0-9]/g, "") })}
                           data-testid="create-tenant-phone" />
                  </label>
                  <label className="text-xs flex flex-col">
                    <span>Owner name (optional)</span>
                    <input className="kb-input" value={createForm.owner_name}
                           onChange={(e) => setCreateForm({ ...createForm, owner_name: e.target.value })}
                           data-testid="create-tenant-owner-name" />
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <label className="text-xs flex flex-col">
                    <span>Plan</span>
                    <select className="kb-input" value={createForm.plan_slug}
                            onChange={(e) => setCreateForm({ ...createForm, plan_slug: e.target.value })}
                            data-testid="create-tenant-plan">
                      <option value="starter">Starter</option>
                      <option value="pro">Pro</option>
                      <option value="enterprise">Enterprise</option>
                    </select>
                  </label>
                  <label className="text-xs flex flex-col">
                    <span>Custom domain (optional)</span>
                    <input className="kb-input" value={createForm.custom_domain} placeholder="store.brand.com"
                           onChange={(e) => setCreateForm({ ...createForm, custom_domain: e.target.value.toLowerCase() })}
                           data-testid="create-tenant-domain" />
                  </label>
                </div>
                <button onClick={createTenant} disabled={busy === "create"}
                        className="kb-btn-primary w-full text-sm inline-flex items-center justify-center gap-1.5"
                        data-testid="create-tenant-submit">
                  {busy === "create" ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Create tenant
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function AddonRow({ addon, onSave, onCancel, busy }) {
  const [editing, setEditing] = useState(false);
  const [price, setPrice] = useState(addon.price_at_purchase || 0);
  const [qty, setQty] = useState(addon.quantity || 1);
  const isBusyUpdate = busy === `update-${addon.feature_key}`;
  const isBusyCancel = busy === `cancel-${addon.feature_key}`;

  return (
    <div className="border border-[hsl(var(--kb-border))] p-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`addon-row-${addon.feature_key}`}>
      <div className="min-w-0">
        <div className="font-medium">{addon.feature_name}</div>
        <div className="text-xs text-[hsl(var(--kb-muted))] font-mono truncate">{addon.feature_key}</div>
        {addon.granted_by_super_admin && (
          <div className="text-xs text-purple-700 mt-1">Granted by super admin{addon.grant_note ? ` — ${addon.grant_note}` : ""}</div>
        )}
      </div>
      {editing ? (
        <div className="flex items-end gap-2">
          <label className="text-xs flex flex-col">Price ₹
            <input type="number" value={price} onChange={(e) => setPrice(e.target.value)} className="kb-input w-24" data-testid={`addon-price-${addon.feature_key}`} />
          </label>
          <label className="text-xs flex flex-col">Qty
            <input type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} className="kb-input w-16" data-testid={`addon-qty-${addon.feature_key}`} />
          </label>
          <button className="kb-btn-primary text-xs" disabled={isBusyUpdate}
                  onClick={async () => {
                    await onSave({ price_at_purchase: Number(price), quantity: Number(qty) || 1 });
                    setEditing(false);
                  }}
                  data-testid={`addon-save-${addon.feature_key}`}>
            {isBusyUpdate ? <Loader2 size={10} className="animate-spin" /> : "Save"}
          </button>
          <button className="kb-btn-outline text-xs" onClick={() => setEditing(false)}>Cancel</button>
        </div>
      ) : (
        <div className="flex items-center gap-3">
          <div className="text-sm">₹{addon.price_at_purchase} × {addon.quantity || 1}</div>
          <button className="kb-btn-outline text-xs" onClick={() => setEditing(true)} data-testid={`addon-edit-${addon.feature_key}`}>
            <Edit3 size={10} /> Edit
          </button>
          <button className="kb-btn-outline text-xs" disabled={isBusyCancel} onClick={onCancel} data-testid={`addon-cancel-${addon.feature_key}`}>
            {isBusyCancel ? <Loader2 size={10} className="animate-spin" /> : <X size={10} />}
          </button>
        </div>
      )}
    </div>
  );
}
